// FacilityTypesList.tsx
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { Card } from "@shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Input } from "@shared/ui/input";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { Edit, Plus, Search, Settings, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  useAddFacility,
  useDeleteFacility,
  useFacility,
  useSearchFacilityType,
  useUpdateFacility,
} from "../../hooks";
import { FacilityTypeForm } from "../create/create";
import { logEvent } from "@shared/analytics/analytics";

interface FacilityType {
  id: string;
  name: string;
  description: string;
}

interface FormData {
  name: string;
  description: string;
}

export function FacilityTypesList() {
  const remoteUtilityId = getRemoteUtilityId();

  const { data: facilityData, refetch } = useFacility({
    remote_utility_id: remoteUtilityId,
    config_level: "facility_type",
    show_inactive: "true",
  });

  const addFacilityMutation = useAddFacility();
  const updateFacilityMutation = useUpdateFacility();
  const deleteFacilityMutation = useDeleteFacility();

  // State declarations
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false); // New state for delete dialog
  const [facilityToDelete, setFacilityToDelete] = useState<FacilityType | null>(
    null
  ); // Track which facility to delete
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
  });
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: searchData, refetch: refetchSearch } = useSearchFacilityType({
    remote_utility_id: remoteUtilityId,
    search_data: searchTerm,
    search_field: "facility_type",
  });

  // Use search results if search term exists, otherwise use all data
  const currentData = searchTerm ? searchData : facilityData;
  const facilityTypes: FacilityType[] =
    currentData?.result?.map((item: any) => ({
      id: item.code,
      name: item.name,
      description: item.description || "",
    })) || [];

  const resetForm = () => {
    setFormData({ name: "", description: "" });
    setEditingId(null);
  };

  const showToast = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default"
  ) => {
    toast({ title, description, variant });
  };

  const handleSaveAdd = async (data: any) => {
    if (!data.name) return;

    try {
      await addFacilityMutation.mutateAsync({
        name: data.name,
        code: data.name,
        remote_utility_id: remoteUtilityId,
        description: data.description,
      });
      logEvent("facility_type_added", { name: data.name });
      resetForm();
      setIsAddDialogOpen(false);
      refetch();
      refetchSearch();
      showToast("Success", "Facility type added successfully");
    } catch (error) {
      logEvent("facility_type_add_failed", { error: error.message });
      showToast(
        "Error",
        `Failed to add facility type: ${error.message}`,
        "destructive"
      );
    }
  };

  const handleSaveEdit = async (formData) => {
    if (!editingId || !formData.name) return;

    try {
      await updateFacilityMutation.mutateAsync({
        name: formData.name,
        code: editingId,
        remote_utility_id: remoteUtilityId,
        description: formData.description,
      });
      logEvent("facility_type_updated", { id: editingId, name: formData.name });
      resetForm();
      setIsEditDialogOpen(false);
      refetch();
      showToast("Success", "Facility type updated successfully");
    } catch (error) {
      logEvent("facility_type_update_failed", { id: editingId });
      showToast("Error", "Failed to update facility type", "destructive");
    }
  };

  const handleEdit = (facilityType: FacilityType) => {
    setEditingId(facilityType.id);
    setFormData({
      name: facilityType.name,
      description: facilityType.description,
    });
    setIsEditDialogOpen(true);
  };

  // Modified delete handler to open confirmation dialog
  const handleDeleteClick = (facilityType: FacilityType) => {
    setFacilityToDelete(facilityType);
    setIsDeleteDialogOpen(true);
  };

  // Actual delete function called after confirmation
  const handleConfirmDelete = async () => {
    if (!facilityToDelete) return;

    try {
      await deleteFacilityMutation.mutateAsync({
        remote_utility_id: remoteUtilityId,
        code: facilityToDelete.id,
      });
      logEvent("facility_type_deleted", {
        id: facilityToDelete.id,
        name: facilityToDelete.name,
      });
      refetch();
      showToast("Success", "Facility type deleted successfully");
    } catch (error) {
      logEvent("facility_type_delete_failed", { id: facilityToDelete.id });
      showToast("Error", "Failed to delete facility type", "destructive");
    } finally {
      setIsDeleteDialogOpen(false);
      setFacilityToDelete(null);
    }
  };

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setFacilityToDelete(null);
  };

  const handleCloseDialog = (dialogType: "add" | "edit") => {
    if (dialogType === "add") {
      setIsAddDialogOpen(false);
    } else {
      setIsEditDialogOpen(false);
    }
    resetForm();
  };

  return (
    <div className="space-y-6">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search facility types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          onClick={() => {
            resetForm();
            logEvent("facility_type_add_button_clicked");
            setIsAddDialogOpen(true);
          }}
          className="ml-4"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Facility Type
        </Button>
      </div>

      {/* Add Facility Type Dialog */}
      <Dialog
        open={isAddDialogOpen}
        onOpenChange={() => handleCloseDialog("add")}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Facility Type</DialogTitle>
          </DialogHeader>
          <FacilityTypeForm
            isVisible={true}
            mode="add"
            formData={formData}
            onFormChange={setFormData}
            onSave={handleSaveAdd}
            onCancel={() => handleCloseDialog("add")}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Facility Type Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={() => handleCloseDialog("edit")}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Facility Type</DialogTitle>
          </DialogHeader>
          <FacilityTypeForm
            isVisible={true}
            mode="edit"
            formData={formData}
            onFormChange={setFormData}
            onSave={handleSaveEdit}
            onCancel={() => handleCloseDialog("edit")}
            editingFacility={facilityTypes.find((ft) => ft.id === editingId)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Facility Type"
        itemName={facilityToDelete?.name}
        description={`Are you sure you want to delete the facility type "${facilityToDelete?.name}"? This action cannot be undone and may affect related configurations.`}
        confirmText="Delete Facility Type"
        isLoading={deleteFacilityMutation.isPending}
      />

      {/* Facility Types List */}
      <div className="space-y-4">
        {facilityTypes.map((facilityType) => (
          <Card
            key={facilityType.id}
            className="p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col items-start gap-2 flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-lg font-semibold text-blue-600">
                    {facilityType.name}
                  </span>
                </div>
                {facilityType.description && (
                  <div className="text-left w-full">
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md border-l-4 border-blue-200">
                      <span className="font-medium text-gray-800">
                        Description:{" "}
                      </span>
                      {facilityType.description}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(facilityType)}
                  disabled={
                    addFacilityMutation.isPending ||
                    updateFacilityMutation.isPending
                  }
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteClick(facilityType)} // Updated to use confirmation dialog
                  disabled={true}
                  tooltip={"This functionality is currently not available."}
                  tooltipPosition="left"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Empty state */}
      {facilityTypes.length === 0 && (
        <Card className="p-8">
          <div className="text-center">
            <p className="text-gray-500">
              {searchTerm ? (
                <div className="text-center py-6 border rounded-md bg-muted/30">
                  <Settings className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
                  <h3 className="font-medium mb-1">No Facility Types</h3>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4">
                    No facility types found matching "{searchTerm}". Try
                    adjusting your search or create a new facility type.
                  </p>
                  <Button
                    onClick={() => {
                      resetForm();
                      setIsAddDialogOpen(true);
                    }}
                    className="ml-4"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Facility Type
                  </Button>
                </div>
              ) : (
                "No facility types found"
              )}
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
